import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, CircularProgress, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    TextField, ButtonBase,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BalanceIcon from "@mui/icons-material/Balance";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
    FB, FB_LIGHT, BORDER, T1, T2,
    G200, G300, G500, G700, G900, WHITE,
    btnGhostSx, btnPrimarySx, btnOutlineSx, inputSx,
} from "./ReportDetailsConstants";
import { BALANCE_SHEET_DUMMY_ROWS } from "./balanceSheetDummyData";

const getTodayDate = () => new Date().toISOString().split("T")[0];

// Indian FY runs April 1 -> March 31 — same default range convention used by
// Interest Calculation / Depreciation Report's default filters.
const getFYDates = () => {
    const today = new Date();
    const month = today.getMonth(); // 0 = Jan … 3 = Apr
    const year = today.getFullYear();
    const fyStartYear = month >= 3 ? year : year - 1;
    return {
        fromDate: `${fyStartYear}-04-01`,
        toDate: `${fyStartYear + 1}-03-31`,
    };
};

const REPORT_TYPE_OPTIONS = [
    { value: "trial_balance", label: "Trial Balance Sheet", icon: ListAltOutlinedIcon },
    // "Trading Account" option removed — selecting Profit & Loss now also
    // fetches the Trading data.
    // { value: "trading_account", label: "Trading Account", icon: StorefrontOutlinedIcon },
    { value: "profit_loss", label: "Profit & Loss Account", icon: TrendingUpOutlinedIcon },
    { value: "balance_sheet", label: "Balance Sheet", icon: BalanceIcon },
];
const VIEW_TYPE_OPTIONS = [
    { value: "vertical", label: "Vertical", icon: ViewAgendaOutlinedIcon },
    { value: "horizontal", label: "Horizontal", icon: ViewColumnOutlinedIcon },
];
const FORMAT_OPTIONS = [
    { value: "summary", label: "Summary", icon: SummarizeOutlinedIcon },
    { value: "details", label: "Details", icon: ListAltOutlinedIcon },
];

// ── SelectCard — a single pickable option rendered as an elevated card with
// an icon, label, and a check badge when selected. Replaces the plain MUI
// radio list with a richer, more "designed" selector while keeping the same
// underlying value/onChange contract. Defined at MODULE scope (not inside
// BalanceSheetFilterDialog) — defining a component inside another component's
// body makes React treat it as a brand-new component type on every parent
// render, forcing every card to unmount+remount on each click. That killed
// the CSS transition and the ripple, which is what caused the visible
// "jerk"/flash when picking an option. ───────────────────────────────────────
const SelectCard = ({ selected, icon: Icon, label, onClick, dense }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            flex: 1,
            minWidth: dense ? 0 : 140,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.75,
            px: 1.75,
            py: dense ? 1.25 : 1.5,
            borderRadius: "12px",
            border: `1.5px solid ${selected ? FB : G300}`,
            background: selected ? FB_LIGHT : WHITE,
            position: "relative",
            textAlign: "left",
            transition: "border-color .15s ease, background-color .15s ease",
            "&:hover": { borderColor: FB, background: selected ? FB_LIGHT : G200 },
        }}
    >
        {selected && (
            <CheckCircleIcon sx={{ position: "absolute", top: 8, right: 8, fontSize: 17, color: FB }} />
        )}
        <Icon sx={{ fontSize: dense ? 19 : 22, color: selected ? FB : G500 }} />
        <Typography sx={{ fontSize: dense ? 12.5 : 13.5, fontWeight: 700, color: selected ? FB : G900, lineHeight: 1.25, pr: 2 }}>
            {label}
        </Typography>
    </ButtonBase>
);

// ── BalanceSheetFilterDialog — mirrors the VB.NET screen's "Select Type Of
// Report" popup exactly: Report Type radios (Trial Balance Sheet / Trading
// Account / Profit & Loss Account / Balance Sheet, defaulting to Balance
// Sheet since this screen IS the Balance Sheet report), View Type
// (Vertical/Horizontal), Format Selection (Summary/Details), From/To Date,
// OK/Cancel footer. The right-hand "error steps" panel from the original
// screen isn't applicable here (this dialog only configures/exports the
// Balance Sheet), so it's omitted rather than faked. ─────────────────────────
function BalanceSheetFilterDialog({ open, onClose, onOk }) {
    const fyDates = getFYDates();
    const [reportType, setReportType] = useState("balance_sheet");
    const [viewType, setViewType] = useState("vertical");
    const [format, setFormat] = useState("summary");
    const [fromDate, setFromDate] = useState(fyDates.fromDate);
    const [toDate, setToDate] = useState(fyDates.toDate);

    useEffect(() => {
        if (open) {
            const d = getFYDates();
            setReportType("balance_sheet");
            setViewType("vertical");
            setFormat("summary");
            setFromDate(d.fromDate);
            setToDate(d.toDate);
        }
    }, [open]);

    // Trial Balance Sheet is only available in Horizontal view.
    const effectiveViewType = reportType === "trial_balance" ? "horizontal" : viewType;

    const handleOk = () => {
        onOk({ reportType, viewType: effectiveViewType, format, fromDate, toDate });
        onClose();
    };

    const sectionLabelSx = { fontSize: 11, fontWeight: 700, color: T2, mb: 1, letterSpacing: ".4px", textTransform: "uppercase" };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: "16px", border: `1px solid ${G300}`, boxShadow: "0 20px 60px rgba(13,33,55,0.18)", overflow: "hidden" } }}
        >
            {/* ── Header — brand-navy banner instead of a plain white bar, for a more polished, "designed" first impression ── */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 3, py: 2.25,
                    background: `linear-gradient(135deg, ${FB} 0%, #0D2137 100%)`,
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <BalanceIcon sx={{ fontSize: 21, color: WHITE }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: WHITE, lineHeight: 1.2 }}>Select Type Of Report</Typography>
                            <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.75)", mt: "2px" }}>Configure Balance Sheet parameters</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: WHITE, background: "rgba(255,255,255,0.12)", "&:hover": { background: "rgba(255,255,255,0.22)" } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 3, pb: 1.5, background: G200 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

                    {/* ── Select Type Of Report ── */}
                    <Box>
                        <Typography sx={sectionLabelSx}>Select Type Of Report</Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                            {REPORT_TYPE_OPTIONS.map((opt) => (
                                <SelectCard
                                    key={opt.value}
                                    selected={reportType === opt.value}
                                    icon={opt.icon}
                                    label={opt.label}
                                    onClick={() => setReportType(opt.value)}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        {/* ── View Type ── */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={sectionLabelSx}>View Type</Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {VIEW_TYPE_OPTIONS
                                    .filter((opt) => reportType !== "trial_balance" || opt.value === "horizontal")
                                    .map((opt) => (
                                    <SelectCard key={opt.value} dense
                                        selected={effectiveViewType === opt.value}
                                        icon={opt.icon}
                                        label={opt.label}
                                        onClick={() => setViewType(opt.value)}
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* ── Format Selection ── */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={sectionLabelSx}>Format</Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {FORMAT_OPTIONS.map((opt) => (
                                    <SelectCard key={opt.value} dense
                                        selected={format === opt.value}
                                        icon={opt.icon}
                                        label={opt.label}
                                        onClick={() => setFormat(opt.value)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* ── From Date / To Date ── */}
                    <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                            <CalendarMonthOutlinedIcon sx={{ fontSize: 15, color: FB }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T2, letterSpacing: ".4px", textTransform: "uppercase" }}>Reporting Period</Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px" }}>From Date</Typography>
                                {/* Dates can only be changed inside the CURRENT financial year (1 Apr – 31 Mar), From <= To */}
                                <TextField type="date" fullWidth size="small" value={fromDate}
                                    inputProps={{ min: fyDates.fromDate, max: toDate || fyDates.toDate }}
                                    onChange={(e) => { const v = e.target.value; if (v && v >= fyDates.fromDate && v <= (toDate || fyDates.toDate)) setFromDate(v); }}
                                    sx={inputSx} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 500, color: T2, mb: "6px" }}>To Date</Typography>
                                <TextField type="date" fullWidth size="small" value={toDate}
                                    inputProps={{ min: fromDate || fyDates.fromDate, max: fyDates.toDate }}
                                    onChange={(e) => { const v = e.target.value; if (v && v >= (fromDate || fyDates.fromDate) && v <= fyDates.toDate) setToDate(v); }}
                                    sx={inputSx} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.25, gap: 1.25, background: G200, borderTop: `1px solid ${G300}` }}>
                <Button onClick={onClose} sx={btnGhostSx} fullWidth>
                    Cancel
                </Button>
                <Button onClick={handleOk} sx={{ ...btnPrimarySx, boxShadow: `0 6px 16px ${FB}40` }} fullWidth>
                    Generate Report
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Dummy Balance Sheet rows — same raw schema as the real ledger export
// (GrpHead, GrpType, SchNo, GrpName, SubGrpName, FirmName, ClBal): GrpType
// "Cr" = liability side, "Dr" = asset side. Full row set lives in
// balanceSheetDummyData.js (kept out of this file since it's large).
const DUMMY_BALANCE_SHEET_ROWS = BALANCE_SHEET_DUMMY_ROWS;

// Rolls the raw rows up into one entry per GrpName (schedule group), summing
// ClBal within each group and splitting by GrpType — same "group -> amount"
// shape the PDF template consumes, built from the raw row list above so the
// totals can never drift out of sync with the row data.
function buildBalanceSheetData(rows) {
    const rollup = (grpType) => {
        const byGroup = new Map();
        rows.filter((r) => r.GrpType === grpType).forEach((r) => {
            byGroup.set(r.GrpName, (byGroup.get(r.GrpName) || 0) + Number(r.ClBal || 0));
        });
        return Array.from(byGroup.entries()).map(([group, amount]) => ({
            group,
            amount: Math.round(Math.abs(amount) * 100) / 100,
        }));
    };

    const liabilities = rollup("Cr");
    const assets = rollup("Dr");
    const totalLiabilities = Math.round(liabilities.reduce((s, l) => s + l.amount, 0) * 100) / 100;
    const totalAssets = Math.round(assets.reduce((s, a) => s + a.amount, 0) * 100) / 100;

    // Groups the raw rows by GrpType into "Cr" (left/Credit side) and "Dr"
    // (right/Debit side) buckets — each bucket further split by GrpName into
    // its own line items (FirmName + ClBal) plus a groupTotal, so the PDF can
    // render two side-by-side schedule columns straight from this shape.
    // groupTotal/total are summed from the SIGNED ClBal values first and only
    // abs'd at the end — summing already-absoluted amounts would flip
    // negative contra rows (e.g. "PROFIT AND LOSS A/C") positive instead of
    // netting them, inflating the total (must stay in sync with the
    // liabilities/assets rollup above). A row with no SubGrpName/FirmName
    // (e.g. "Opening Balance"/"Balance Error") has no item to show — it's
    // just the group heading + amount — so it's excluded from `items` and
    // folded into the group via `extra` instead, avoiding a duplicate-looking
    // line under its own heading.
    const buildSide = (grpType) => {
        const byGroup = new Map();
        rows.filter((r) => r.GrpType === grpType).forEach((r) => {
            if (!byGroup.has(r.GrpName)) byGroup.set(r.GrpName, { items: [], extra: 0 });
            const bucket = byGroup.get(r.GrpName);
            const clBal = Number(r.ClBal || 0);
            if (!r.SubGrpName && !r.FirmName) {
                bucket.extra += clBal;
            } else {
                bucket.items.push({
                    subGroup: r.SubGrpName,
                    firmName: r.FirmName,
                    amount: Math.round(Math.abs(clBal) * 100) / 100,
                    clBal,
                });
            }
        });
        const groups = Array.from(byGroup.entries()).map(([groupName, { items, extra }]) => ({
            groupName,
            items,
            extra: Math.round(extra * 100) / 100,
            groupTotal: Math.round(Math.abs(items.reduce((s, i) => s + i.clBal, 0) + extra) * 100) / 100,
        }));
        const sideTotal = Math.round(groups.reduce((s, g) => s + g.groupTotal, 0) * 100) / 100;
        return { groups, total: sideTotal };
    };

    const Cr = buildSide("Cr");
    const Dr = buildSide("Dr");

    return { rows, liabilities, assets, totalLiabilities, totalAssets, Cr, Dr };
}

const DUMMY_BALANCE_SHEET_DATA = buildBalanceSheetData(DUMMY_BALANCE_SHEET_ROWS);

// Side labels per report type — the Cr ("left") / Dr ("right") rollup is shared
// by all reports, only the wording changes:
//   Balance Sheet -> Liabilities / Assets
//   Trial Balance -> Credit / Debit
//   Profit & Loss -> Income / Expenses
function getSideLabels(reportType, plSection) {
    // Profit & Loss report, "Profit & Loss Account" section: Profit / Loss sides
    // (the "Trading Account" section keeps Income / Expenses).
    if (reportType === "profit_loss" && plSection === "profit_loss") {
        return { Cr: "Profit", Dr: "Loss", crSingular: "profit", drSingular: "loss", totalCr: "TOTAL PROFIT", totalDr: "TOTAL LOSS" };
    }
    if (reportType === "trial_balance") {
        return { Cr: "Credit", Dr: "Debit", crSingular: "credit", drSingular: "debit", totalCr: "TOTAL CREDIT", totalDr: "TOTAL DEBIT" };
    }
    if (reportType === "profit_loss") {
        return { Cr: "Income", Dr: "Expenses", crSingular: "income", drSingular: "expense", totalCr: "TOTAL INCOME", totalDr: "TOTAL EXPENSES" };
    }
    return { Cr: "Liabilities", Dr: "Assets", crSingular: "liability", drSingular: "asset", totalCr: "TOTAL LIABILITIES", totalDr: "TOTAL ASSETS" };
}

// Re-groups a side's (Cr/Dr) groups[].items[] by SubGrpName, same drill-down
// shape as LedgerDetails.js's Groupwise Summary report (GrpName -> SubGrpName
// -> clients). Each group's flat item list (subGroup/firmName/amount) is
// split into sub-group buckets, each with its own client list and total.
function buildDrillDownGroups(side) {
    if (!side?.groups) return [];
    return side.groups.map((group) => {
        const bySubGroup = new Map();
        (group.items || []).forEach((item) => {
            const key = item.subGroup || group.groupName;
            if (!bySubGroup.has(key)) bySubGroup.set(key, []);
            bySubGroup.get(key).push(item);
        });
        // Sub-group totals are kept SIGNED (not abs'd per-client then summed)
        // — abs'ing each client independently and summing breaks the
        // sum-of-parts whenever a sub-group mixes positive and negative
        // ClBal rows (e.g. +100 and -30 would abs to 100 + 30 = 130 instead
        // of netting to 70). Only abs once, at the very end, same as
        // group.groupTotal does — every level must net signed children first.
        const subGroups = Array.from(bySubGroup.entries()).map(([subGrpName, clients]) => {
            const signedTotal = clients.reduce((s, c) => s + Number(c.clBal || 0), 0);
            return {
                subGrpName,
                clients,
                signedTotal,
                totalBalance: Math.round(Math.abs(signedTotal) * 100) / 100,
            };
        });
        // A row with no SubGrpName/FirmName (e.g. "Opening Balance"/"Balance
        // Error") has no item and was folded into `extra` instead — surface
        // it as its own pseudo-subgroup so the drill-down list still sums to
        // the group total shown one level up, instead of silently
        // swallowing that amount (which is what made group vs. sub-group
        // totals look mismatched).
        if (group.extra) {
            subGroups.push({
                // A group with no client items at all (e.g. "Opening Stock" /
                // "Opening Balance" / "Balance Error") shows under its own name,
                // without the "(Opening Balance / Adjustment)" suffix.
                subGrpName: (group.items || []).length === 0 ? group.groupName : `${group.groupName} (Opening Balance / Adjustment)`,
                clients: [],
                signedTotal: group.extra,
                totalBalance: Math.round(Math.abs(group.extra) * 100) / 100,
            });
        }
        return {
            groupName: group.groupName,
            subGroups,
            totalBalance: group.groupTotal,
        };
    });
}

// ── BalanceSheetReport — Account Reports section. Opens the "Select Type Of
// Report" filter dialog (VB.NET screen parity) on entry, same as
// InterestCalculationReport/DepreciationReport open their own filter dialogs
// on first mount. The dialog's chosen filters are shown as chips; "Export
// PDF" still posts the balance sheet data to the PDF microservice, same
// request shape as InterestCalculationReport.js/DepreciationReport.js's
// handleExportPDF, with its own dedicated docId ("balance-sheet") —
// unchanged from before. ──────────────────────────────────────────────────────
export default function BalanceSheetReport({ connectionString, pdfString, openFilterTrigger }) {
    const { user, selectedCompany } = useAuth();
    const [pdfLoading, setPdfLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [filterOpen, setFilterOpen] = useState(true);
    // Same trigger pattern as the other Account Reports: a sidebar click increments
    // openFilterTrigger, which re-opens this report's own filter popup.
    useEffect(() => {
        if (openFilterTrigger > 0) {
            setFilterOpen(true);
        }
    }, [openFilterTrigger]);
    const [appliedFilters, setAppliedFilters] = useState(null);
    // Live Balance Sheet data fetched from GET /api/balance-sheet when
    // "Generate Report" is clicked with reportType = "balance_sheet" — built
    // via the same buildBalanceSheetData() rollup as the dummy dataset, just
    // fed real rows instead of BALANCE_SHEET_DUMMY_ROWS. null until fetched.
    const [balanceSheetData, setBalanceSheetData] = useState(null);
    const [profitLossFigures, setProfitLossFigures] = useState(null);
    // Drill-down state for the Groupwise-Summary-style browser below the
    // totals card: bsSide picks Liabilities (Cr) or Assets (Dr), bsPage picks
    // "groups" (GrpName list) or "clients" (after clicking a SubGrpName),
    // bsSelectedSubGroup holds the clicked sub-group's data for the clients
    // page — same drill-down pattern as LedgerDetails.js's Groupwise Summary
    // report (GrpName -> SubGrpName -> clients).
    const [bsSide, setBsSide] = useState("Cr");
    const [bsPage, setBsPage] = useState("groups");
    const [bsSelectedSubGroup, setBsSelectedSubGroup] = useState(null);

    // Profit & Loss report: rows come for GrpHead 1 (Trading Account) and
    // GrpHead 2 (Profit & Loss Account). They are shown as two sections so the
    // P&L groups (indirect income/expenses etc.) are not mixed into Trading.
    const [plSection, setPlSection] = useState("trading");
    const sideLabels = getSideLabels(appliedFilters?.reportType, plSection);
    const displayData = (appliedFilters?.reportType === "profit_loss" && balanceSheetData)
        ? buildBalanceSheetData(
            (balanceSheetData.rows || []).filter((r) =>
                plSection === "trading" ? Number(r.GrpHead) === 1 : Number(r.GrpHead) === 2)
        )
        : balanceSheetData;

    const REPORT_TYPE_LABELS = {
        trial_balance: "Trial Balance Sheet",
        trading_account: "Trading Account",
        profit_loss: "Profit & Loss Account",
        balance_sheet: "Balance Sheet",
    };

    // "Generate Report" — for reportType = "balance_sheet", calls the real
    // GET /api/balance-sheet endpoint (Node.js port of the VB.NET ProfitLoss()
    // function) instead of using the dummy dataset. companyId/branchId come
    // from the logged-in company; fromDate/toDate from the filter dialog.
    const handleApplyFilters = async (filters) => {
        setAppliedFilters(filters);
        setBsSide("Cr");
        setBsPage("groups");
        setBsSelectedSubGroup(null);
        setPlSection("trading");

        // Balance Sheet, Trial Balance and Profit & Loss are all served by the
        // live API (scoped to the selected company); only unknown types fall
        // back to the dummy dataset.
        if (!["balance_sheet", "trial_balance", "profit_loss"].includes(filters.reportType)) {
            // Other report types (Trial Balance / Trading / P&L) aren't wired
            // to a live API yet — keep them on the dummy dataset so the
            // screen still renders something instead of breaking.
            setBalanceSheetData(buildBalanceSheetData(DUMMY_BALANCE_SHEET_ROWS));
            setProfitLossFigures(null);
            return;
        }

        const companyId = selectedCompany?.CompId;
        if (!companyId) {
            setSnackbar({ open: true, message: "No company selected.", severity: "error" });
            return;
        }

        setReportLoading(true);
        try {
            const res = await axios.get(`${connectionString}/api/balance-sheet`, {
                params: {
                    companyId,
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    reportType: filters.reportType,
                    userId: user?.UserId || undefined,
                    branchId: selectedCompany?.BranchId || undefined,
                },
            });

            const data = res.data?.data;
            let rows = Array.isArray(data?.rows) ? data.rows : [];
            if (filters.reportType === "profit_loss") {
                // Income/Expense groups don't carry Cr/Dr in GrpType — map them
                // so the existing Cr (left) / Dr (right) rollup can list them.
                rows = rows.map((r) => {
                    if (r.GrpType === "Cr" || r.GrpType === "Dr") return r;
                    const t = String(r.GrpType || "");
                    return { ...r, GrpType: /income/i.test(t) ? "Cr" : /expense/i.test(t) ? "Dr" : Number(r.ClBal) > 0 ? "Dr" : "Cr" };
                });
            }
            // The stock valuation (data.stockValuation) is NOT listed on the screen —
            // it is kept in profitLossFigures and added to the PDF only (see
            // handleExportPDF), like the Opening Balance / Balance Error lines.
            setBalanceSheetData(buildBalanceSheetData(rows));
            setProfitLossFigures({
                grossProfitLoss: data?.grossProfitLoss ?? 0,
                netProfitLoss: data?.netProfitLoss ?? 0,
                opBalError: data?.opBalError ?? 0,
                clBalError: data?.clBalError ?? 0,
                grossProfit: data?.grossProfit ?? 0,
                grossLoss: data?.grossLoss ?? 0,
                netProfit: data?.netProfit ?? 0,
                netLoss: data?.netLoss ?? 0,
                stockValuation: data?.stockValuation ?? 0,
            });
        } catch (err) {
            const errMsg = err?.response?.data?.message || err.message || "Failed to generate Balance Sheet";
            setSnackbar({ open: true, message: errMsg, severity: "error" });
            setBalanceSheetData(null);
            setProfitLossFigures(null);
        } finally {
            setReportLoading(false);
        }
    };

    // Summary format: one line per sub-group (not one per client), otherwise the
    // PDF repeats the same sub-group total once for every client in it.
    const collapseSummaryRows = (rows) => {
        const bySub = new Map();
        (rows || []).forEach((r) => {
            const key = `${r.GrpType}|${r.SchNo}|${r.GrpName}|${r.SubGrpName}`;
            if (!bySub.has(key)) bySub.set(key, { ...r, FirmName: "", ClBal: 0 });
            bySub.get(key).ClBal += Number(r.ClBal || 0);
        });
        return Array.from(bySub.values()).map((r) => ({ ...r, ClBal: (Math.round(r.ClBal * 100) / 100).toFixed(2) }));
    };

    // PDF display signs (JSON only — the PDF then prints a plain minus sign, no
    // colour needed). ClBal is Debit - Credit, so the natural balance is a
    // positive on the Debit side (Dr) and a negative on the Credit side (Cr).
    // Display value = ClBal on Dr, -ClBal on Cr, so an OPPOSITE balance (a debit
    // on the Credit side / a credit on the Debit side) comes out negative.
    // Side totals (Cr.total / Dr.total / totalLiabilities / totalAssets) are
    // calculations and are left as they are.
    const toPdfDisplaySigned = (data) => {
        const r2 = (v) => (Math.round(v * 100) / 100) || 0;
        const mapSide = (side, sign) => ({
            ...side,
            groups: (side.groups || []).map((g) => {
                const net = (g.items || []).reduce((s, i) => s + Number(i.clBal || 0), 0) + Number(g.extra || 0);
                return {
                    ...g,
                    items: (g.items || []).map((i) => ({ ...i, amount: r2(sign * Number(i.clBal || 0)), clBal: r2(sign * Number(i.clBal || 0)) })),
                    extra: r2(sign * Number(g.extra || 0)),
                    groupTotal: r2(sign * net),
                };
            }),
        });
        const Cr = mapSide(data.Cr, -1);
        const Dr = mapSide(data.Dr, 1);
        return {
            ...data,
            Cr,
            Dr,
            liabilities: Cr.groups.map((g) => ({ group: g.groupName, amount: g.groupTotal })),
            assets: Dr.groups.map((g) => ({ group: g.groupName, amount: g.groupTotal })),
            rows: (data.rows || []).map((r) => ({
                ...r,
                ClBal: r2((r.GrpType === "Cr" ? -1 : 1) * Number(r.ClBal || 0)).toFixed(2),
            })),
        };
    };

    const handleExportPDF = async () => {
        if (pdfLoading || !balanceSheetData) return;
        // Balance Sheet and Trial Balance Sheet both call the same PDF API
        // ("balance-sheet"); the PDF service tells them apart by reportType.
        // Profit & Loss uses the same payload but its own endpoint (below).
        const pdfReportTypeMap = { balance_sheet: "balance_sheet", trial_balance: "trial_balance_sheet", profit_loss: "profit_loss" };
        const pdfReportType = pdfReportTypeMap[appliedFilters?.reportType];
        if (!pdfReportType) {
            setSnackbar({ open: true, message: "PDF is available only for the Balance Sheet, Trial Balance Sheet and Profit & Loss reports.", severity: "warning" });
            return;
        }
        // Trial Balance Sheet goes to the groupwise-summary endpoint, Profit & Loss to
        // its own endpoint, the Balance Sheet keeps the original one.
        const pdfEndpoint = appliedFilters?.reportType === "profit_loss"
            ? "/api/ledger-pdf/profit-loss-balance-sheet"
            : appliedFilters?.reportType === "trial_balance"
                ? "/api/ledger-pdf/balance-sheet-groupwise-summary"
                : "/api/ledger-pdf/balance-sheet";
        try {
            setPdfLoading(true);

            const printDate = getTodayDate();
            const isSummary = appliedFilters?.format !== "details";
            // Opening Balance error (Assets / Dr side) and Balance Error
            // (Liabilities / Cr side) are added to the PDF data only (not the
            // on-screen report), as their own lines like any other group:
            // blank SubGrpName/FirmName, so buildBalanceSheetData folds them
            // into the group heading + amount and includes them in the totals.
            const opBalError = Number(profitLossFigures?.opBalError) || 0;
            const clBalError = Number(profitLossFigures?.clBalError) || 0;
            // Opening Balance goes at the TOP of the Assets (Dr) side; Balance
            // Error stays at the END of the Liabilities (Cr) side. Groups are
            // listed in the order their first row appears, so row order decides it.
            // Side of the Opening Balance follows its sign: positive -> Credit
            // (Liabilities) side, negative -> Debit (Assets) side. ClBal is
            // -opBalError so it prints as a normal (positive) balancing amount on
            // that side (the PDF display rule is Cr: -ClBal, Dr: ClBal).
            const openingBalanceRows = opBalError !== 0
                ? [{ GrpHead: 3, GrpType: opBalError > 0 ? "Cr" : "Dr", SchNo: 101, GrpName: "Opening Balance", SubGrpName: "", FirmName: "", ClBal: -opBalError }]
                : [];
            const balanceErrorRows = clBalError !== 0
                ? [{ GrpHead: 3, GrpType: "Cr", SchNo: 9998, GrpName: "Balance Error", SubGrpName: "", FirmName: "", ClBal: clBalError }]
                : [];
            const pdfBaseRows = isSummary ? collapseSummaryRows(balanceSheetData.rows) : balanceSheetData.rows;
            // Stock valuation (Balance Sheet only): FIRST line on the Assets (Dr) side
            // of the PDF, as its own line (blank SubGrpName/FirmName, so it is folded
            // into the group heading + amount and counted in the Assets total).
            const stockValuation = appliedFilters?.reportType === "balance_sheet" ? Number(profitLossFigures?.stockValuation) || 0 : 0;
            // The stock valuation is now posted as a Journal Voucher BEFORE the report data is
            // read (backend), so it already comes in the rows (Closing Stock on the Income side,
            // Stock In Hand under the Assets). Adding the separate stock line here as well would
            // count it twice, so it is left empty:
            const stockRows = [];
            // previous: stockRows = stockValuation !== 0 ? [{ ...Opening Stock Dr row... }] : [];
            const pdfBalanceSheetData = toPdfDisplaySigned(buildBalanceSheetData([...stockRows, ...openingBalanceRows, ...pdfBaseRows, ...balanceErrorRows]));

            // Profit & Loss PDF: the flat Cr / Dr lists above mix Trading (GrpHead 1)
            // and Profit & Loss (GrpHead 2) groups, so the PDF service can't tell
            // them apart from them. Send each section separately (same shape as
            // Cr / Dr, plus its own labels and result line) — the existing fields
            // above are kept as they are.
            let pdfSections = {};
            if (appliedFilters?.reportType === "profit_loss") {
                const figs = profitLossFigures || {};
                const sectionData = (grpHead) =>
                    toPdfDisplaySigned(buildBalanceSheetData(pdfBaseRows.filter((r) => Number(r.GrpHead) === grpHead)));
                pdfSections = {
                    // Trading Account: Cr = Income, Dr = Expenses -> Gross Profit / Loss
                    trading: {
                        title: "Trading Account",
                        sideLabels: { Cr: "Income", Dr: "Expenses" },
                        ...sectionData(1),
                        result: figs.grossLoss > 0
                            ? { label: "Gross Loss", type: "loss", amount: figs.grossLoss }
                            : { label: "Gross Profit", type: "profit", amount: figs.grossProfit || 0 },
                    },
                    // Profit & Loss Account: Cr = Profit side, Dr = Loss side -> Net Profit / Loss
                    profitLoss: {
                        title: "Profit & Loss Account",
                        sideLabels: { Cr: "Profit", Dr: "Loss" },
                        ...sectionData(2),
                        result: figs.netLoss > 0
                            ? { label: "Net Loss", type: "loss", amount: figs.netLoss }
                            : { label: "Net Profit", type: "profit", amount: figs.netProfit || 0 },
                    },
                };
            }

            const pdfPayload = {
                // Profit & Loss has its own PDF template (docId "profit-loss"); the
                // Balance Sheet and Trial Balance Sheet keep "balance-sheet".
                // Trial Balance Sheet uses the groupwise-summary template.
                docId: appliedFilters?.reportType === "profit_loss"
                    ? "profit-loss"
                    : appliedFilters?.reportType === "trial_balance"
                        ? "balance-sheet-groupwise-summary"
                        : "balance-sheet",
                userId: user?.UserId || "usr1",
                data: {
                    reportType: pdfReportType,
                    accountName: selectedCompany?.CompanyName || "",
                    printDate,
                    orientation: appliedFilters?.viewType === "horizontal" ? "Horizontal" : "Vertical",
                    reportMode: appliedFilters?.format === "details" ? "Details" : "Summary",
                    ...pdfBalanceSheetData,
                    ...(profitLossFigures || {}),
                    ...pdfSections,
                },
                company: selectedCompany
                    ? {
                        CompId: selectedCompany.CompId,
                        CompanyName: selectedCompany.CompanyName || "",
                        Address: selectedCompany.Address || "",
                        MobileNo: selectedCompany.MobileNo || "",
                        GSTIN: selectedCompany.GSTNo || "",
                    }
                    : {},
            };

            console.log("📄 [Balance Sheet Report] Export PDF payload:", pdfPayload);

            const pdfResp = await axios.post(
                `${pdfString}${pdfEndpoint}`,
                pdfPayload,
                { responseType: "blob" }
            );

            const w = window.open(
                URL.createObjectURL(new Blob([pdfResp.data], { type: "application/pdf" })),
                "_blank"
            );
            if (w) w.focus();
        } catch (err) {
            console.error("PDF Error:", err);
            setSnackbar({ open: true, message: "Failed to generate PDF", severity: "error" });
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <Box sx={{ background: G200, display: "flex", flexDirection: "column", minHeight: "100%" }}>
            {/* ── ERP Header — same compact bar as Ledger Report (58px, 4px accent rail, 36px icon tile) ── */}
            <Box sx={{ background: WHITE, borderBottom: `1px solid ${G300}`, display: "flex", alignItems: "stretch", flexShrink: 0, minHeight: 58 }}>
                <Box sx={{ width: 4, background: FB, flexShrink: 0 }} />
                <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "space-between", px: { xs: 2, sm: 3 }, py: 1.25, gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, background: FB_LIGHT, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <BalanceIcon sx={{ fontSize: 20, color: FB }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: G900, lineHeight: 1.2 }}>Balance Sheet</Typography>
                            <Typography sx={{ fontSize: 11, color: G500, mt: "1px" }}>
                                {appliedFilters ? `${appliedFilters.fromDate} – ${appliedFilters.toDate}` : `As On ${getTodayDate()}`}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        {appliedFilters && balanceSheetData && (
                            <Button onClick={handleExportPDF} disabled={pdfLoading} startIcon={pdfLoading ? <CircularProgress size={13} sx={{ color: FB }} /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />} sx={btnOutlineSx}>
                                Export PDF
                            </Button>
                        )}
                        <Button onClick={() => setFilterOpen(true)} startIcon={<TuneIcon sx={{ fontSize: 15 }} />} sx={btnPrimarySx}>
                            Smart Filter
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ── Toolbar: filter tags row — same G200 strip + small pills as Ledger Report ── */}
            {appliedFilters && (
                <Box sx={{ background: WHITE, borderBottom: `1px solid ${G300}`, flexShrink: 0 }}>
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", background: G200, borderBottom: `1px solid ${G300}` }}>
                        <Typography sx={{ fontSize: 11, color: G500, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".4px", mr: 0.5 }}>Filter:</Typography>
                        {[
                            REPORT_TYPE_LABELS[appliedFilters.reportType],
                            appliedFilters.viewType === "vertical" ? "Vertical" : "Horizontal",
                            appliedFilters.format === "summary" ? "Summary" : "Details",
                            `${appliedFilters.fromDate} – ${appliedFilters.toDate}`,
                        ].filter(Boolean).map((tag) => (
                            <Box key={tag} sx={{ px: 1.25, py: "2px", background: FB_LIGHT, borderRadius: "4px", border: `1px solid ${G300}` }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: FB }}>{tag}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {reportLoading ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <Box sx={{ textAlign: "center" }}>
                        <CircularProgress sx={{ color: FB, mb: 2 }} />
                        <Typography sx={{ fontSize: 14, color: G500, fontWeight: 500 }}>Loading Balance Sheet...</Typography>
                    </Box>
                </Box>
            ) : appliedFilters && balanceSheetData ? (
                <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
                    {/* ── KPI Strip — same compact single-row card as Ledger Report's KPI strip ── */}
                    {/* Not shown for the Balance Sheet report (display only — the figures are still calculated). */}
                    <Box sx={{ display: appliedFilters.reportType === "balance_sheet" ? "none" : "block", width: "100%", maxWidth: 900, mx: "auto" }}>
                        <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "8px", overflow: "hidden" }}>
                            <Box sx={{ display: "flex", px: { xs: 2, sm: 3 }, py: 1.5, overflowX: "auto" }}>
                                {[
                                    { label: sideLabels.totalDr, value: displayData.totalAssets, accent: true },
                                    { label: sideLabels.totalCr, value: displayData.totalLiabilities, accent: false },
                                    ...(profitLossFigures && appliedFilters.reportType === "profit_loss" ? [
                                        // Profit & Loss report: Trading section -> Gross result,
                                        // Profit & Loss section -> Net result
                                        ...(plSection === "trading"
                                            ? [profitLossFigures.grossLoss > 0
                                                ? { label: "GROSS LOSS", value: profitLossFigures.grossLoss, accent: false, tone: "#C62828" }
                                                : { label: "GROSS PROFIT", value: profitLossFigures.grossProfit, accent: false, tone: "#1B5E20" }]
                                            : [profitLossFigures.netLoss > 0
                                                ? { label: "NET LOSS", value: profitLossFigures.netLoss, accent: false, tone: "#C62828" }
                                                : { label: "NET PROFIT", value: profitLossFigures.netProfit, accent: false, tone: "#1B5E20" }]),
                                    ] : profitLossFigures && appliedFilters.reportType === "balance_sheet" ? [
                                        { label: "NET PROFIT/LOSS", value: profitLossFigures.netProfitLoss, accent: false, tone: profitLossFigures.netProfitLoss >= 0 ? "#1B5E20" : "#C62828" },
                                        { label: "OP. BAL. ERROR", value: profitLossFigures.opBalError, accent: false, tone: profitLossFigures.opBalError === 0 ? "#1B5E20" : "#B26A00" },
                                        { label: "BALANCE ERROR", value: profitLossFigures.clBalError, accent: false, tone: profitLossFigures.clBalError === 0 ? "#1B5E20" : "#B26A00" },
                                    ] : []),
                                ].map((item, i, arr) => (
                                    <Box key={item.label} sx={{ pr: { xs: 2.5, sm: 3.5 }, pl: i === 0 ? 0 : { xs: 2.5, sm: 3.5 }, borderRight: i < arr.length - 1 ? `1px solid ${G300}` : "none", flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: G500, textTransform: "uppercase", letterSpacing: ".6px", lineHeight: 1, mb: "4px" }}>{item.label}</Typography>
                                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: item.tone || (item.accent ? FB : G900), fontFamily: "monospace", lineHeight: 1 }}>
                                            {item.value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* ── DRILL-DOWN BROWSER: Groupwise Summary pattern ── */}
                    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}>
                        {/* Profit & Loss report: Trading Account / Profit & Loss Account sections */}
                        {appliedFilters.reportType === "profit_loss" && (
                            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                                {[
                                    { key: "trading", label: "Trading Account" },
                                    { key: "profit_loss", label: "Profit & Loss Account" },
                                ].map((tab) => (
                                    <ButtonBase
                                        key={tab.key}
                                        onClick={() => { setPlSection(tab.key); setBsSide("Cr"); setBsPage("groups"); setBsSelectedSubGroup(null); }}
                                        sx={{
                                            px: 2, py: 1, borderRadius: "10px",
                                            border: `1.5px solid ${plSection === tab.key ? FB : G300}`,
                                            background: plSection === tab.key ? FB_LIGHT : WHITE,
                                            transition: "border-color .15s ease, background-color .15s ease",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: plSection === tab.key ? FB : G700 }}>{tab.label}</Typography>
                                    </ButtonBase>
                                ))}
                            </Box>
                        )}

                        {/* Both sides are shown together (left / right) — no side tabs. */}
                        {(() => {
                            const fmtAmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            const BalBadge = ({ value, size = "md" }) => {
                                const isNeg = value < 0;
                                return (
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", px: size === "sm" ? "8px" : "10px", py: size === "sm" ? "2px" : "4px", borderRadius: "7px", background: isNeg ? "#FFF0F0" : FB_LIGHT, border: `1px solid ${isNeg ? "#FFCDD2" : "#BBDEFB"}`, flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: size === "sm" ? 11 : 12, fontWeight: 700, color: isNeg ? "#C62828" : FB, fontVariantNumeric: "tabular-nums" }}>
                                            {fmtAmt(Math.abs(value))}
                                        </Typography>
                                    </Box>
                                );
                            };

                            // DISPLAY ONLY (calculations/totals above are untouched), same
                            // rule as the PDF: ClBal is Debit - Credit, so the natural
                            // balance is positive on the Debit side (Dr) and negative on
                            // the Credit side (Cr). Display = ClBal on Dr, -ClBal on Cr,
                            // so an opposite balance is shown negative.
                            const signFor = (sideKey) => (sideKey === "Cr" ? -1 : 1);
                            const shownFor = (sideKey) => (v) => (Math.round(signFor(sideKey) * Number(v || 0) * 100) / 100) || 0;
                            const groupSigned = (g) => g.subGroups.reduce((s, sg) => s + Number(sg.signedTotal || 0), 0);

                            // ── PAGE: CLIENTS (after clicking a SubGrpName) ────────
                            if (bsPage === "clients" && bsSelectedSubGroup) {
                                const shown = shownFor(bsSide);
                                return (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", maxWidth: 900, mx: "auto" }}>
                                        <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                <Button onClick={() => { setBsPage("groups"); setBsSelectedSubGroup(null); }}
                                                    startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                                                    sx={{ ...btnGhostSx, height: 32, px: 1.5, fontSize: 12 }}>
                                                    Back
                                                </Button>
                                                <Box sx={{ width: 1, height: 20, background: G300, mx: 0.5 }} />
                                                <Typography sx={{ fontSize: 12, color: G500 }}>{bsSide === "Cr" ? sideLabels.Cr : sideLabels.Dr}</Typography>
                                                <Typography sx={{ fontSize: 12, color: G300 }}>›</Typography>
                                                <Typography sx={{ fontSize: 12, color: G700 }}>{bsSelectedSubGroup.groupName}</Typography>
                                                <Typography sx={{ fontSize: 12, color: G300 }}>›</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: FB }}>{bsSelectedSubGroup.subGrpName}</Typography>
                                            </Box>
                                            <BalBadge value={shown(bsSelectedSubGroup.signedTotal)} />
                                        </Box>

                                        <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", overflow: "hidden" }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: "8px", background: `linear-gradient(90deg, ${FB_LIGHT} 0%, #F0F7FF 100%)`, borderBottom: `1px solid ${G300}` }}>
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: G700, letterSpacing: ".5px", textTransform: "uppercase" }}>Client Name</Typography>
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: G700, letterSpacing: ".5px", textTransform: "uppercase" }}>Balance</Typography>
                                            </Box>

                                            {bsSelectedSubGroup.clients.length === 0 ? (
                                                <Box sx={{ py: "2rem", textAlign: "center" }}>
                                                    <Typography sx={{ fontSize: 13, color: G500 }}>No clients in this sub-group.</Typography>
                                                </Box>
                                            ) : bsSelectedSubGroup.clients.map((client, cIdx) => (
                                                <Box key={`${client.firmName}-${cIdx}`}
                                                    sx={{
                                                        display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: "11px", background: cIdx % 2 === 0 ? WHITE : "#FAFAFA", borderBottom: cIdx < bsSelectedSubGroup.clients.length - 1 ? `1px solid ${G200}` : "none",
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: G700 }}>{client.firmName}</Typography>
                                                    <BalBadge value={shown(client.clBal ?? client.amount ?? 0)} size="sm" />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            }

                            // ── PAGE: GROUPS — one card per side, left (Cr) and right (Dr) ──
                            const renderSide = (sideKey) => {
                                const side = displayData[sideKey];
                                const drillGroups = buildDrillDownGroups(side);
                                const totalSubGroups = drillGroups.reduce((s, g) => s + g.subGroups.length, 0);
                                const totalClients = drillGroups.reduce((s, g) => s + g.subGroups.reduce((ss, sg) => ss + sg.clients.length, 0), 0);
                                const shown = shownFor(sideKey);
                                const grandSigned = drillGroups.reduce((s, g) => s + groupSigned(g), 0);
                                const sideTitle = sideKey === "Cr" ? sideLabels.Cr : sideLabels.Dr;

                                if (drillGroups.length === 0) {
                                    return (
                                        <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", p: 3, textAlign: "center" }}>
                                            <Typography sx={{ fontSize: 13, color: G500 }}>No {sideKey === "Cr" ? sideLabels.crSingular : sideLabels.drSingular} groups found for this period.</Typography>
                                        </Box>
                                    );
                                }

                                let srNo = 0;
                                // Each side is one half of a single card (the card border /
                                // shadow is on the wrapper below), so no own border here.
                                return (
                                    <Box sx={{ background: WHITE, display: "flex", flexDirection: "column", height: "100%" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, background: `linear-gradient(90deg, ${FB} 0%, #1a5fce 100%)` }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: WHITE }}>{sideTitle}</Typography>
                                                {/* Count pill and total badge are not shown for the Balance Sheet report */}
                                                {appliedFilters.reportType !== "balance_sheet" && (
                                                    <Box sx={{ px: "8px", py: "2px", background: "rgba(255,255,255,0.18)", borderRadius: "6px" }}>
                                                        <Typography sx={{ fontSize: 11, color: WHITE, fontWeight: 600 }}>{totalSubGroups} sub-groups · {totalClients} clients</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {appliedFilters.reportType !== "balance_sheet" && <BalBadge value={shown(grandSigned)} />}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                        <Table size="small" sx={{ tableLayout: "fixed" }}>
                                            <TableHead>
                                                <TableRow sx={{ background: FB_LIGHT }}>
                                                    <TableCell sx={{ width: 40, fontWeight: 700, fontSize: 11, color: G700, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `2px solid ${G300}`, py: 1.25, pl: 2 }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: G700, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `2px solid ${G300}`, py: 1.25, pl: 2 }}>Sub Group</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: G700, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `2px solid ${G300}`, py: 1.25, pr: 2 }}>Balance</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {drillGroups.map((group) => (
                                                    <React.Fragment key={`grp-${sideKey}-${group.groupName}`}>
                                                        <TableRow sx={{ background: G200 }}>
                                                            <TableCell sx={{ borderBottom: `1px solid ${G300}`, py: "7px", pl: 2 }} />
                                                            <TableCell sx={{ borderBottom: `1px solid ${G300}`, py: "7px", pl: 2 }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <Box sx={{ width: 3, height: 16, background: G700, borderRadius: 1, flexShrink: 0 }} />
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: G900, textTransform: "uppercase", letterSpacing: ".3px" }}>{group.groupName}</Typography>
                                                                    {!(group.subGroups.length === 1 && group.subGroups[0].clients.length === 0) && (
<Box sx={{ px: "6px", py: "1px", background: G300, borderRadius: "4px" }}>
                                                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: G700 }}>{group.subGroups.length} sub-group{group.subGroups.length !== 1 ? "s" : ""}</Typography>
                                                                    </Box>
)}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ borderBottom: `1px solid ${G300}`, py: "7px", pr: 2 }}>
                                                                <BalBadge value={shown(groupSigned(group))} size="sm" />
                                                            </TableCell>
                                                        </TableRow>

                                                        {!(group.subGroups.length === 1 && group.subGroups[0].clients.length === 0) && group.subGroups.map((sg, sgIdx) => {
                                                            srNo++;
                                                            return (
                                                                <TableRow key={`${sideKey}-${group.groupName}-${sg.subGrpName}`}
                                                                    onClick={() => {
                                                                        setBsSide(sideKey);
                                                                        setBsSelectedSubGroup({ groupName: group.groupName, ...sg });
                                                                        setBsPage("clients");
                                                                    }}
                                                                    sx={{
                                                                        cursor: "pointer", background: sgIdx % 2 === 0 ? WHITE : "#FAFBFD",
                                                                        "& td": { borderBottom: `1px solid ${G200}` },
                                                                        "& td:first-of-type": { borderLeft: `3px solid transparent` },
                                                                        "&:hover td": { background: "#EEF4FF" },
                                                                        "&:hover td:first-of-type": { borderLeft: `3px solid ${FB}` },
                                                                    }}
                                                                >
                                                                    <TableCell sx={{ py: 1.25, pl: 2, color: G500, fontSize: 12 }}>{srNo}</TableCell>
                                                                    <TableCell sx={{ py: 1.25, pl: 2 }}>
                                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: FB }}>{sg.subGrpName}</Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ py: 1.25, pr: 2 }}>
                                                                        <BalBadge value={shown(sg.signedTotal)} size="sm" />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        </Box>

                                        {/* Grand Total — pinned to the bottom so both sides' totals line up */}
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, background: FB_LIGHT, borderTop: `2px solid ${G300}` }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box sx={{ width: 3, height: 18, background: FB, borderRadius: 1, flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: G900 }}>Grand Total</Typography>
                                                <Typography sx={{ fontSize: 11, color: G500 }}>· {drillGroups.length} group{drillGroups.length !== 1 ? "s" : ""} · {totalSubGroups} sub-groups</Typography>
                                            </Box>
                                            <BalBadge value={shown(grandSigned)} />
                                        </Box>
                                    </Box>
                                );
                            };

                            // ── TRIAL BALANCE: one Groupwise-Summary-style list (GrpName ->
                            // SubGrpName), both sides together in a single card — no
                            // separate Credit / Debit sections. Each group keeps the sign
                            // rule of its own side; footer shows total Debit / total Credit.
                            if (appliedFilters.reportType === "trial_balance") {
                                const tbGroups = [
                                    ...buildDrillDownGroups(displayData.Cr).map((g) => ({ ...g, sideKey: "Cr" })),
                                    ...buildDrillDownGroups(displayData.Dr).map((g) => ({ ...g, sideKey: "Dr" })),
                                ].sort((a, b) => String(a.groupName).localeCompare(String(b.groupName)));

                                if (tbGroups.length === 0) {
                                    return (
                                        <Box sx={{ background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", p: 3, textAlign: "center" }}>
                                            <Typography sx={{ fontSize: 13, color: G500 }}>No groups found for this period.</Typography>
                                        </Box>
                                    );
                                }

                                const tbSubGroups = tbGroups.reduce((s, g) => s + g.subGroups.length, 0);
                                const tbClients = tbGroups.reduce((s, g) => s + g.subGroups.reduce((ss, sg) => ss + sg.clients.length, 0), 0);
                                let tbSrNo = 0;
                                // Groupwise-Summary look: ONE balance per row — the net
                                // Debit - Credit (ClBal), shown as an amount with a DR / CR
                                // suffix. Both sides sit in the same list (no separate
                                // Debit / Credit columns or totals); the grand total is the
                                // net of everything (0 when the books balance).
                                const tbGrand = tbGroups.reduce((s, g) => s + groupSigned(g), 0);
                                const TbBadge = ({ value, size = "md" }) => {
                                    const v = Math.round(Number(value || 0) * 100) / 100;
                                    const isCr = v < 0;
                                    return (
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: size === "sm" ? "8px" : "10px", py: size === "sm" ? "2px" : "4px", borderRadius: "7px", background: isCr ? "#FFF0F0" : FB_LIGHT, border: `1px solid ${isCr ? "#FFCDD2" : "#BBDEFB"}`, flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: size === "sm" ? 11 : 12, fontWeight: 700, color: isCr ? "#C62828" : FB, fontVariantNumeric: "tabular-nums" }}>
                                                ₹ {fmtAmt(Math.abs(v))}
                                            </Typography>
                                            {v !== 0 && (
                                                <Typography sx={{ fontSize: 9, fontWeight: 700, color: isCr ? "#C62828" : FB }}>{isCr ? "CR" : "DR"}</Typography>
                                            )}
                                        </Box>
                                    );
                                };
                                return (
                                    <Box sx={{ width: "100%", background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, background: `linear-gradient(90deg, ${FB} 0%, #1a5fce 100%)` }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: WHITE }}>Trial Balance</Typography>
                                                <Box sx={{ px: "8px", py: "2px", background: "rgba(255,255,255,0.18)", borderRadius: "6px" }}>
                                                    <Typography sx={{ fontSize: 11, color: WHITE, fontWeight: 600 }}>{tbSubGroups} sub-groups · {tbClients} clients</Typography>
                                                </Box>
                                            </Box>
                                            <TbBadge value={tbGrand} />
                                        </Box>

                                        <Table size="small" sx={{ tableLayout: "fixed" }}>
                                            <TableHead>
                                                <TableRow sx={{ background: FB_LIGHT }}>
                                                    <TableCell sx={{ width: 40, fontWeight: 700, fontSize: 11, color: G700, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `2px solid ${G300}`, py: 1.25, pl: 2 }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: G700, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `2px solid ${G300}`, py: 1.25, pl: 2 }}>Sub Group</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: G700, textTransform: "uppercase", letterSpacing: ".5px", borderBottom: `2px solid ${G300}`, py: 1.25, pr: 2 }}>Balance</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {tbGroups.map((group) => {
                                                    return (
                                                        <React.Fragment key={`tb-${group.sideKey}-${group.groupName}`}>
                                                            <TableRow sx={{ background: G200 }}>
                                                                <TableCell sx={{ borderBottom: `1px solid ${G300}`, py: "7px", pl: 2 }} />
                                                                <TableCell sx={{ borderBottom: `1px solid ${G300}`, py: "7px", pl: 2 }}>
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                        <Box sx={{ width: 3, height: 16, background: G700, borderRadius: 1, flexShrink: 0 }} />
                                                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: G900, textTransform: "uppercase", letterSpacing: ".3px" }}>{group.groupName}</Typography>
                                                                        {!(group.subGroups.length === 1 && group.subGroups[0].clients.length === 0) && (
<Box sx={{ px: "6px", py: "1px", background: G300, borderRadius: "4px" }}>
                                                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: G700 }}>{group.subGroups.length} sub-group{group.subGroups.length !== 1 ? "s" : ""}</Typography>
                                                                        </Box>
)}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ borderBottom: `1px solid ${G300}`, py: "7px", pr: 2 }}>
                                                                    <TbBadge value={groupSigned(group)} size="sm" />
                                                                </TableCell>
                                                            </TableRow>

                                                            {!(group.subGroups.length === 1 && group.subGroups[0].clients.length === 0) && group.subGroups.map((sg, sgIdx) => {
                                                                tbSrNo++;
                                                                return (
                                                                    <TableRow key={`tb-${group.sideKey}-${group.groupName}-${sg.subGrpName}`}
                                                                        onClick={() => {
                                                                            setBsSide(group.sideKey);
                                                                            setBsSelectedSubGroup({ groupName: group.groupName, ...sg });
                                                                            setBsPage("clients");
                                                                        }}
                                                                        sx={{
                                                                            cursor: "pointer", background: sgIdx % 2 === 0 ? WHITE : "#FAFBFD",
                                                                            "& td": { borderBottom: `1px solid ${G200}` },
                                                                            "& td:first-of-type": { borderLeft: `3px solid transparent` },
                                                                            "&:hover td": { background: "#EEF4FF" },
                                                                            "&:hover td:first-of-type": { borderLeft: `3px solid ${FB}` },
                                                                        }}
                                                                    >
                                                                        <TableCell sx={{ py: 1.25, pl: 2, color: G500, fontSize: 12 }}>{tbSrNo}</TableCell>
                                                                        <TableCell sx={{ py: 1.25, pl: 2 }}>
                                                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: FB }}>{sg.subGrpName}</Typography>
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ py: 1.25, pr: 2 }}>
                                                                            <TbBadge value={sg.signedTotal} size="sm" />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}

                                                <TableRow sx={{ background: FB_LIGHT, "& td": { borderTop: `2px solid ${G300}`, borderBottom: "none", py: 1.5 } }}>
                                                    <TableCell sx={{ pl: 2 }} />
                                                    <TableCell sx={{ pl: 2 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <Box sx={{ width: 3, height: 18, background: FB, borderRadius: 1, flexShrink: 0 }} />
                                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: G900 }}>Grand Total</Typography>
                                                            <Typography sx={{ fontSize: 11, color: G500 }}>· {tbGroups.length} group{tbGroups.length !== 1 ? "s" : ""} · {tbSubGroups} sub-groups</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ pr: 2 }}>
                                                        <TbBadge value={tbGrand} />
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Box>
                                );
                            }

                            // Profit & Loss report: Expenses (Dr) on the LEFT, Income (Cr) on the
                            // RIGHT (swapped). Every other report keeps Cr left | Dr right.
                            const leftSideKey = appliedFilters.reportType === "profit_loss" ? "Dr" : "Cr";
                            const rightSideKey = leftSideKey === "Dr" ? "Cr" : "Dr";
                            return (
                                // Both sides in ONE card, divided by a line.
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "stretch", background: WHITE, border: `1px solid ${G300}`, borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
                                    <Box sx={{ borderRight: { xs: "none", md: `1px solid ${G300}` }, borderBottom: { xs: `1px solid ${G300}`, md: "none" } }}>
                                        {renderSide(leftSideKey)}
                                    </Box>
                                    <Box>
                                        {renderSide(rightSideKey)}
                                    </Box>
                                </Box>
                            );
                        })()}
                    </Box>
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                    <Box sx={{ textAlign: "center", maxWidth: 400 }}>
                        <Box sx={{
                            width: 64, height: 64, borderRadius: "14px",
                            background: FB_LIGHT,
                            display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2,
                        }}>
                            <AccountBalanceOutlinedIcon sx={{ fontSize: 30, color: FB }} />
                        </Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: G900, mb: 1 }}>No Report Generated</Typography>
                        <Typography sx={{ fontSize: 13, color: T2, lineHeight: 1.7, mb: 2.5 }}>
                            Select a report type, view, format, and date range to configure and generate the Balance Sheet.
                        </Typography>
                        <Button onClick={() => setFilterOpen(true)} startIcon={<TuneIcon sx={{ fontSize: 15 }} />} sx={btnPrimarySx}>
                            Configure Report
                        </Button>
                    </Box>
                </Box>
            )}

            <BalanceSheetFilterDialog
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onOk={handleApplyFilters}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
